// Argus — useColumnConfig: persist column widths + order per (agent, parentPath) în localStorage.
// Cheie: argus.fileExplorer.columns.<agentId>.<parentPath>
// Value: JSON { widths: { name: 320, size: 100, modifiedAt: 160 }, order: ['name','size','modifiedAt'] }
// La schimbare folder → fall back la per-agent default → fall back la global default.

import { useCallback, useEffect, useState } from 'react';

export type ColumnKey = 'name' | 'size' | 'modifiedAt';

export interface ColumnConfig {
  widths: Partial<Record<ColumnKey, number>>;
  order: ColumnKey[];
}

const STORAGE_PREFIX = 'argus.fileExplorer.columns';
const DEFAULT_WIDTHS: Record<ColumnKey, number> = {
  name: 320,
  size: 80,
  modifiedAt: 120,
};
const MIN_WIDTHS: Record<ColumnKey, number> = {
  name: 120,
  size: 80,
  modifiedAt: 120,
};
const DEFAULT_ORDER: ColumnKey[] = ['name', 'size', 'modifiedAt'];

export function getMinWidth(col: ColumnKey): number {
  return MIN_WIDTHS[col];
}

function key(agentId: string, parentPath: string): string {
  // Sanitize parentPath pentru localStorage key (drop \\\\, \\, /).
  const sanitized = parentPath.replace(/[\\\/]+/g, '_') || 'root';
  return `${STORAGE_PREFIX}.${agentId}.${sanitized}`;
}
function keyAgent(agentId: string): string {
  return `${STORAGE_PREFIX}.${agentId}._default`;
}

function load(agentId: string, parentPath: string): ColumnConfig {
  if (typeof window === 'undefined') return { widths: {}, order: DEFAULT_ORDER };
  const tryRead = (k: string): ColumnConfig | null => {
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as ColumnConfig;
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed.order)) {
        // Migrare config vechi cu 'icon' col → drop.
        const validKeys = new Set<ColumnKey>(['name', 'size', 'modifiedAt']);
        const cleanOrder = parsed.order.filter((k): k is ColumnKey => validKeys.has(k as ColumnKey));
        if (cleanOrder.length === 0) return null;
        return { widths: parsed.widths ?? {}, order: cleanOrder };
      }
    } catch { /* ignore */ }
    return null;
  };
  return tryRead(key(agentId, parentPath))
    ?? tryRead(keyAgent(agentId))
    ?? { widths: {}, order: DEFAULT_ORDER };
}

function save(agentId: string, parentPath: string, config: ColumnConfig): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key(agentId, parentPath), JSON.stringify(config));
    // Salvează și ca default per-agent pentru next folder fără config dedicat.
    localStorage.setItem(keyAgent(agentId), JSON.stringify(config));
  } catch { /* quota exceeded — ignore */ }
}

export interface UseColumnConfigResult {
  widths: Record<ColumnKey, number>;
  order: ColumnKey[];
  setWidth: (col: ColumnKey, w: number) => void;
  setOrder: (newOrder: ColumnKey[]) => void;
  reset: () => void;
}

export function useColumnConfig(agentId: string, parentPath: string): UseColumnConfigResult {
  const [config, setConfig] = useState<ColumnConfig>(() => load(agentId, parentPath));

  // Re-load când se schimbă agentId sau parentPath.
  useEffect(() => {
    setConfig(load(agentId, parentPath));
  }, [agentId, parentPath]);

  const setWidth = useCallback((col: ColumnKey, w: number) => {
    setConfig((prev) => {
      const next: ColumnConfig = { ...prev, widths: { ...prev.widths, [col]: Math.max(MIN_WIDTHS[col], Math.round(w)) } };
      save(agentId, parentPath, next);
      return next;
    });
  }, [agentId, parentPath]);

  const setOrder = useCallback((newOrder: ColumnKey[]) => {
    setConfig((prev) => {
      const next: ColumnConfig = { ...prev, order: newOrder };
      save(agentId, parentPath, next);
      return next;
    });
  }, [agentId, parentPath]);

  const reset = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(key(agentId, parentPath));
    }
    setConfig({ widths: {}, order: DEFAULT_ORDER });
  }, [agentId, parentPath]);

  // Resolved widths (default fallback).
  const widths: Record<ColumnKey, number> = {
    name: config.widths.name ?? DEFAULT_WIDTHS.name,
    size: config.widths.size ?? DEFAULT_WIDTHS.size,
    modifiedAt: config.widths.modifiedAt ?? DEFAULT_WIDTHS.modifiedAt,
  };

  return { widths, order: config.order, setWidth, setOrder, reset };
}
